import { Check, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

import type { CustomerStatus, CustomerSummary, KycStatus } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import { useCustomerList } from './queries'

/** Long enough that a name typed at speed is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300

/** Below this nothing is fetched — two characters is roughly where a name search stops matching everyone. */
const MIN_SEARCH_LENGTH = 2

/**
 * Finding a customer and confirming them by name — inline, never inside a second closed control.
 *
 * This replaces two things this application used to do separately: a "search box, then open a
 * `Select` to actually pick the row" idiom (`/loans/new`'s member search, before this component
 * existed) that reviewers read as broken — the matches are fetched and then hidden inside a second
 * dropdown nobody thinks to open — and a hand-rolled inline list duplicated wherever a screen got
 * this right on its own (opening a savings account, adding a member to a group). One component now
 * owns the pattern: type a name or customer number, the matches render as a list directly under the
 * box, and choosing one replaces the search with a removable chip.
 *
 * KYC filtering is never implicit. `listCustomers` does not filter by KYC status on its own — every
 * caller that wants only approved customers (every account-opening context does; the API refuses an
 * account for anyone else) passes `kycStatus="Approved"` explicitly, and the empty state then says
 * so in words rather than showing a silent empty list.
 */
export interface MemberPickerProps {
  selected: CustomerSummary | null
  onSelect: (customer: CustomerSummary | null) => void
  /** Base id for the search input and its help text — keep it unique per instance on the page. */
  id: string
  /** Field label. Defaults to "Member". */
  label?: string
  placeholder?: string
  /** Scopes the search to one branch. Omitted searches every branch the caller can see. */
  branchId?: string | null
  /** Narrows to customers in this status. */
  status?: CustomerStatus
  /**
   * Narrows to customers with this KYC status. Pass `"Approved"` for any context that is about to
   * hold an account or a membership against this customer — the empty state then explains why,
   * rather than reading as a search that simply found nobody.
   */
  kycStatus?: KycStatus
  /** Customer ids left out of the results — typically people already part of the thing being built. */
  excludeCustomerIds?: ReadonlySet<string>
  /** Replaces the default help text under the search box. */
  helpText?: ReactNode
  pageSize?: number
}

export function MemberPicker({
  selected,
  onSelect,
  id,
  label = 'Member',
  placeholder = 'Name or customer number',
  branchId,
  status,
  kycStatus,
  excludeCustomerIds,
  helpText,
  pageSize = 10,
}: MemberPickerProps) {
  const [text, setText] = useState('')
  const [term, setTerm] = useState('')
  const listRef = useRef<HTMLUListElement | null>(null)

  useEffect(() => {
    const trimmed = text.trim()
    if (trimmed === term) {
      return
    }

    const timer = window.setTimeout(() => setTerm(trimmed), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [text, term])

  const searching = term.length >= MIN_SEARCH_LENGTH

  const query = useCustomerList({
    search: term === '' ? undefined : term,
    branchId: branchId ?? undefined,
    status,
    kycStatus,
    page: 1,
    pageSize,
  })

  const results = useMemo(() => {
    const items = query.data?.items ?? []
    if (!excludeCustomerIds || excludeCustomerIds.size === 0) {
      return items
    }
    return items.filter((customer) => !excludeCustomerIds.has(customer.id))
  }, [query.data, excludeCustomerIds])

  const searchId = `${id}-search`
  const helpId = `${id}-help`
  const listboxId = `${id}-results`

  /** Roving focus across the result rows — arrow keys move it, Home/End jump to the ends. */
  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [],
    )
    if (buttons.length === 0) {
      return
    }

    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)

    const focusAt = (index: number) => {
      event.preventDefault()
      buttons[(index + buttons.length) % buttons.length]?.focus()
    }

    switch (event.key) {
      case 'ArrowDown':
        focusAt(current === -1 ? 0 : current + 1)
        break
      case 'ArrowUp':
        focusAt(current === -1 ? buttons.length - 1 : current - 1)
        break
      case 'Home':
        focusAt(0)
        break
      case 'End':
        focusAt(buttons.length - 1)
        break
      default:
        break
    }
  }

  if (selected) {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-brand-fill/40 bg-brand-fill-subtle p-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Check className="size-4 text-brand-text" aria-hidden />
              <span className="font-medium text-heading">{selected.fullName}</span>
              <StatusBadge status={selected.status} />
              <StatusBadge status={selected.kycStatus} />
            </div>
            <p className="font-mono text-xs text-body">{selected.customerNumber}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSelect(null)
              setText('')
              setTerm('')
            }}
          >
            <X />
            Change
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={searchId}>{label}</Label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
          aria-hidden
        />
        <Input
          id={searchId}
          value={text}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          className="pl-9"
          onChange={(event) => setText(event.target.value)}
          aria-describedby={helpId}
          role="combobox"
          aria-expanded={searching}
          aria-controls={listboxId}
          aria-autocomplete="list"
        />
      </div>
      <p id={helpId} className="text-xs text-muted">
        {helpText ?? memberPickerHelpText(kycStatus)}
      </p>

      {!searching ? null : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="The customer search failed"
        />
      ) : query.isPending ? (
        <div className="space-y-1.5">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : results.length === 0 ? (
        <p className="text-xs text-muted">
          No member matches "{term}"
          {kycStatus === 'Approved' ? ' — note that only approved customers can hold accounts.' : '.'}
        </p>
      ) : (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={`${label} search results`}
          onKeyDown={onListKeyDown}
          className="max-h-64 divide-y divide-border-subtle overflow-y-auto rounded-card border border-border-subtle"
        >
          {results.map((customer, index) => (
            <li key={customer.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={false}
                tabIndex={index === 0 ? 0 : -1}
                onClick={() => onSelect(customer)}
                className="flex min-h-11 w-full flex-wrap items-center justify-between gap-2 p-3 text-left transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover"
              >
                <div className="min-w-0 space-y-0.5">
                  <span className="block truncate text-sm font-medium text-heading">
                    {customer.fullName}
                  </span>
                  <span className="block font-mono text-2xs text-muted">
                    {customer.customerNumber}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <StatusBadge status={customer.status} />
                  <StatusBadge status={customer.kycStatus} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The help line for a KYC-gated picker, kept in one place so every account-opening screen says it the same way. */
function memberPickerHelpText(kycStatus: KycStatus | undefined): string {
  return kycStatus === 'Approved'
    ? 'Two characters or more. Only customers whose KYC file is approved can be chosen — someone not yet approved will not appear here.'
    : 'Two characters or more. Type a name or customer number, then choose from the list.'
}
